import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
  Button,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ContinueReadingDrawer from './ContinueReadingDrawer';

interface ContinueReadingNotificationProps {
  readonly open: boolean;
  readonly chapterTitle?: string;
  readonly onContinue: () => void;
  readonly onClose: () => void;
}

function ContinueReadingNotification({
  open,
  chapterTitle,
  onContinue,
  onClose,
}: ContinueReadingNotificationProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const useDrawer = useMediaQuery(theme.breakpoints.down('md'));

  if (useDrawer) {
    return (
      <ContinueReadingDrawer
        open={open}
        chapterTitle={chapterTitle}
        onContinue={onContinue}
        onClose={onClose}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby='continue-reading-dialog-title'
      fullWidth
      maxWidth='md'
      sx={{
        '& .MuiDialog-container': { alignItems: 'flex-start' },
        '& .MuiDialog-paper': {
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: (muiTheme) => muiTheme.palette.background.paper,
          color: (muiTheme) => muiTheme.palette.text.primary,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        component='div'
        sx={(muiTheme) => ({
          pr: 8,
          py: 3,
          borderBottom: `1px solid ${muiTheme.palette.divider}`,
        })}
      >
        <Typography
          id='continue-reading-dialog-title'
          variant='h5'
          component='h2'
          sx={{ fontWeight: 700 }}
        >
          {t('continueReading.notification.title')}
        </Typography>
        <IconButton
          aria-label={t('continueReading.notification.close')}
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ textAlign: 'center', px: 6, pt: 4, pb: 2 }}>
        <Typography variant='h5' sx={{ fontWeight: 600 }}>
          {chapterTitle ?? ''}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{ px: 6, pt: 2, pb: 5, gap: 2, justifyContent: 'center' }}
      >
        <Button
          variant='outlined'
          size='large'
          onClick={onClose}
          sx={(muiTheme) => ({
            minWidth: 200,
            color: muiTheme.palette.text.primary,
            borderColor: muiTheme.palette.divider,
            '&:hover': {
              borderColor: muiTheme.palette.text.secondary,
              bgcolor: muiTheme.palette.action.hover,
            },
          })}
        >
          {t('continueReading.notification.close')}
        </Button>
        <Button
          variant='contained'
          color='success'
          size='large'
          onClick={onContinue}
          sx={{ minWidth: 200 }}
        >
          {t('continueReading.notification.button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ContinueReadingNotification;
